// biome-ignore-all lint/correctness/useHookAtTopLevel: useDiscord is static for the life of an app session, so it's ok to break React's Rules of Hooks

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { setDiscordState, setInfoBannerMessage } from 'src/common/actions/app';
import { loginWithOauth } from 'src/common/actions/auth';
import { openNowPlayingDialog } from 'src/common/actions/dialog';
import { logClientError, logClientInfo } from 'src/common/actions/logging';
import { showMessage } from 'src/common/actions/messagePopover';
import { DISCORD_DEBUG_MODE_ENABLED } from 'src/common/constants/experiments/discord';
import {
  DISCORD_RICH_PRESENCE_DETAILS_BROWSING,
  DISCORD_RICH_PRESENCE_DETAILS_PLAYING,
} from 'src/common/constants/localizations/discord';
import { HOME_BROWSE_PATH } from 'src/common/constants/paths';
import { playerStatuses } from 'src/common/constants/playerStatuses';
import { manualDiscordModes } from 'src/common/constants/queryParams';
import useActions from 'src/common/hooks/useActions';
import { useCustomHistory } from 'src/common/hooks/useCustomHistory';
import useExperiment from 'src/common/hooks/useExperiment';
import useOnMount from 'src/common/hooks/useOnMount';
import usePrevious from 'src/common/hooks/usePrevious';
import { useStores } from 'src/common/providers';
import { LocationAndLocalizationContext } from 'src/common/providers/LocationAndLocalizationProvider';
import {
  selectBreakpoint,
  selectIsDiscord,
  selectIsFord,
  selectManualDiscordMode,
} from 'src/common/selectors/app';
import { isXXSmall } from 'src/common/utils/breakpoints';
import { useBrowsiesDefaultLandingPage } from '../../../../hooks/useBrowsiesDefaultLandingPage';
import { splashScreenTypes } from '../../constants';
import { useDiscordGeorestrictionsBanner } from '../useDiscordGeorestrictionsBanner';

const playerStatusEventTriggers = {
  [playerStatuses.playing]: true,
  // these will all be treated as a "pause" event
  [playerStatuses.idle]: true,
};
const actionsList = {
  logClientError,
  logClientInfo,
  setDiscordState,
  loginWithOauth,
  openNowPlayingDialog,
  showMessage,
  setInfoBannerMessage,
};
const defaultHookReturnValue = {};

export function useDiscord(history, routeProps) {
  const isDiscord = useSelector(selectIsDiscord);
  const isFord = useSelector(selectIsFord);

  // isDiscord is static for the life of an app session, so it's ok to break React's Rules of Hooks here
  if (!isDiscord) {
    return defaultHookReturnValue;
  }

  // isFord is static for the life of an app session, so it's ok to break React's Rules of Hooks here
  if (isFord) {
    if (routeProps.isLanding) {
      history.replace(HOME_BROWSE_PATH);

      return {
        isDiscordReady: true,
        showDiscordSplashScreen: routeProps.isLanding,
      };
    }

    return defaultHookReturnValue;
  }

  const manualDiscordMode = useSelector(selectManualDiscordMode);
  const isUiOnlyDiscordMode = manualDiscordMode === manualDiscordModes.ui;
  const defaultLandingPage = useBrowsiesDefaultLandingPage() || HOME_BROWSE_PATH;
  const actions = useActions(actionsList);

  // Discord UI mode is static for the life of an app session, so it's ok to break React's Rules of Hooks here
  if (isUiOnlyDiscordMode) {
    useOnMount(() => {
      async function initUiMode() {
        const { getMockDiscordState } = await import('../../discordManager');

        actions.setDiscordState(getMockDiscordState());

        if (routeProps.isLanding) {
          history.replace(defaultLandingPage);
        }
      }

      initUiMode();
    });

    return {
      isDiscordReady: true,
      showDiscordSplashScreen: routeProps.isLanding,
    };
  }

  const { playerStore } = useStores();
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const isDebugModeEnabled = useExperiment(DISCORD_DEBUG_MODE_ENABLED);
  const discordManagerRef = useRef();
  const playerStatus = playerStore.playerStatus();
  const previousPlayerStatus = usePrevious(playerStatus);
  const tunedGuideId = playerStore.currentGuideItem()?.playableGuideId;
  const playbackRate = playerStore.playbackRate();
  const nowPlaying = playerStore.metadata();
  const breakpoint = useSelector(selectBreakpoint);
  const hasNowPlayingRejectReason = Boolean(nowPlaying?.rejectReason);
  const [isDiscordReady, setIsDiscordReady] = useState(false);
  const [splashScreenType, setSplashScreenType] = useState(null);

  useOnMount(() => {
    let teardown;

    async function loadDiscordManager() {
      const { discordManager } = await import('../../discordManager');

      discordManagerRef.current = discordManager;
      teardown = discordManager.destroy;

      discordManager.on('authed', () => {
        history.replace(defaultLandingPage);
        discordManager.setRichPresence(getLocalizedText(DISCORD_RICH_PRESENCE_DETAILS_BROWSING));
      });
      discordManager.on('ready', () => setIsDiscordReady(true));
      discordManager.on('maxParticipantsReached', () => {
        setSplashScreenType(splashScreenTypes.maxParticipants);
      });

      await discordManager.init({
        actions,
        isDebugModeEnabled,
      });

      return () => {
        teardown?.();
      };
    }

    function preventRightClick(e) {
      e.preventDefault();
    }

    loadDiscordManager();
    document.addEventListener('contextmenu', preventRightClick);

    return () => {
      document.removeEventListener('contextmenu', preventRightClick);
      teardown?.();
    };
  });

  useCustomHistory();
  useDiscordGeorestrictionsBanner();

  useEffect(() => {
    if (
      discordManagerRef.current?.canControlPlayback &&
      playerStatusEventTriggers[playerStatus] &&
      previousPlayerStatus !== playerStatuses.failed &&
      !hasNowPlayingRejectReason
    ) {
      discordManagerRef.current.setListening({
        guideId: tunedGuideId,
        isPlaying:
          playerStatus === playerStatuses.connecting || playerStatus === playerStatuses.playing,
        playbackRate,
      });
    }
  }, [playerStatus, previousPlayerStatus, tunedGuideId, playbackRate, hasNowPlayingRejectReason]);

  useEffect(() => {
    if (splashScreenType === splashScreenTypes.maxParticipants) {
      return;
    }

    if (isXXSmall(breakpoint)) {
      setSplashScreenType(null);
      return;
    }

    if (tunedGuideId) {
      actions.openNowPlayingDialog();
      return;
    }

    setSplashScreenType(splashScreenTypes.pictureInPicture);
  }, [actions, breakpoint, splashScreenType, tunedGuideId]);

  useEffect(() => {
    if (nowPlaying?.title) {
      discordManagerRef.current?.setRichPresence(
        getLocalizedText(DISCORD_RICH_PRESENCE_DETAILS_PLAYING),
        nowPlaying?.title,
      );
    }
  }, [getLocalizedText, nowPlaying?.title]);

  return useMemo(
    () => ({
      isDiscordReady,
      showDiscordSplashScreen: routeProps.isLanding || !isDiscordReady || splashScreenType,
      splashScreenType,
    }),
    [isDiscordReady, splashScreenType, routeProps.isLanding],
  );
}
